import React, { useMemo, useState, useEffect } from 'react';
import { X, Info, CheckCircle2, XCircle } from 'lucide-react';
import { GerArquivosService } from '../../services/gerArquivosService';
import type { ProdutoCategoriaItem } from '../../types/gerArquivos';
import { logger } from '../../lib/terminalLogger';

type SaveTempDraft = {
  id: string;
  createdAt: string; // ISO
  baseName: string;
  date: string; // YYYY-MM-DD
  clientName: string;
  orderNumber: string; // 5 dígitos
  prod_cat_item_id: number; // ID da categoria (1-4)
  prod_cat_item_name: string; // Nome da categoria para exibição
  quality: string;
};

// Mapeamento de qualidades por categoria (pode ser expandido)
const CATEGORY_QUALITIES: Record<number, string[]> = {
  1: ['PREMIUM', 'CLASSICO', 'LIGHT', 'LABEL HOT'], // DTF TEXTIL
  3: ['COM TACK', 'SEM TACK'], // SUBLIMACAO
  2: ['BRILHO', 'FOSCO'], // DTF UV
  4: ['PERSONALIZADA', 'SUBLIMAÇÃO TOTAL'] // CAMISETA
};

// Regras de dimensão por categoria (pode ser expandido)
const CATEGORY_RULES: Record<number, string[]> = {
  1: ['Regras de dimensão: 57,5cm x 205cm'], // DTF TEXTIL
  3: ['Regras de dimensão: 157,0cm x 450cm'], // SUBLIMACAO
  2: [
    'Regras de dimensão [metro]: 58,5cm x 100cm',
    'Regras de dimensão [unidade]: 40,5cm x 28,5cm'
  ], // DTF UV
  4: ['Regras de dimensão: sem regras'] // CAMISETA
};

const SAVE_TEMP_STORAGE_KEY = 'save-temp-drafts';

function loadDrafts(): SaveTempDraft[] {
  try {
    const raw = localStorage.getItem(SAVE_TEMP_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function saveDraft(draft: SaveTempDraft) {
  const all = loadDrafts();
  const next = [...all, draft];
  localStorage.setItem(SAVE_TEMP_STORAGE_KEY, JSON.stringify(next));
}

function parseBaseName(input: string) {
  // Normalizar input: remover acentos e caracteres especiais antes da validação
  // Isso permite aceitar tanto "JOÃOPAULO" quanto "JOAOPAULO" automaticamente
  const normalizedInput = (input || '')
    .trim()
    .normalize('NFD')                    // Decompõe caracteres (Ã → A + ~)
    .replace(/[\u0300-\u036f]/g, '')      // Remove diacríticos (acentos)
    .toUpperCase();                        // Converte para maiúsculas
  
  // ✅ Permite hífen (-) no nome do cliente para casos como "MELI-48017" ou "CLAUDINEI-48012"
  const regex = /^(?<date>\d{8})(?<client>[A-Z0-9-]+?)(?<order>\d{5})$/;
  const match = normalizedInput.match(regex);
  if (!match || !match.groups) {
    return { ok: false as const, value: input.trim(), error: 'Formato inválido' };
  }
  const dateRaw = match.groups.date;
  const clientRaw = match.groups.client;
  const orderRaw = match.groups.order;

  const yyyy = dateRaw.slice(0, 4);
  const mm = dateRaw.slice(4, 6);
  const dd = dateRaw.slice(6, 8);
  const iso = `${yyyy}-${mm}-${dd}`;
  const dateObj = new Date(iso);
  const isValidDate = !isNaN(dateObj.getTime());
  if (!isValidDate) return { ok: false as const, value: input.trim(), error: 'Data inválida' };

  return {
    ok: true as const,
    value: normalizedInput, // Retorna o valor normalizado (sem acentos)
    parsed: {
      baseName: normalizedInput, // Armazena sempre normalizado
      dateIso: iso,
      client: clientRaw,
      order: orderRaw
    }
  };
}

export function SaveTempModal({ onClose, onSaved }: { onClose: () => void; onSaved?: (draft: SaveTempDraft) => void }) {
  const [fileName, setFileName] = useState('');
  const [categoriaId, setCategoriaId] = useState<number | null>(null);
  const [categorias, setCategorias] = useState<ProdutoCategoriaItem[]>([]);
  const [carregandoCategorias, setCarregandoCategorias] = useState(true);
  
  const qualities = useMemo(() => CATEGORY_QUALITIES[categoriaId || 0] || [], [categoriaId]);
  const [quality, setQuality] = useState<string>(qualities[0] || '');
  const [saving, setSaving] = useState(false);
  const [showRules, setShowRules] = useState(false);

  // Buscar categorias da API
  useEffect(() => {
    const fetchCategorias = async () => {
      setCarregandoCategorias(true);
      try {
        const cats = await GerArquivosService.listarProdutoCategorias();
        setCategorias(cats);
      } catch (error) {
        logger.error('Erro ao buscar categorias:', error);
        setCategorias([]);
      } finally {
        setCarregandoCategorias(false);
      }
    };
    fetchCategorias();
  }, []);

  const parsed = useMemo(() => parseBaseName(fileName), [fileName]);
  const isValid = parsed.ok && categoriaId !== null && !!quality;

  useEffect(() => {
    if (!qualities.includes(quality)) {
      setQuality(qualities[0] ?? '');
    }
  }, [qualities, quality]);

  const handleSave = async () => {
    if (!isValid || saving || !parsed.ok || categoriaId === null) return;
    setSaving(true);
    try {
      const categoria = categorias.find(c => c.id === categoriaId);
      const draft: SaveTempDraft = {
        id: crypto.randomUUID(),
        createdAt: new Date().toISOString(),
        baseName: parsed.parsed.baseName,
        date: parsed.parsed.dateIso,
        clientName: parsed.parsed.client,
        orderNumber: parsed.parsed.order,
        prod_cat_item_id: categoriaId,
        prod_cat_item_name: categoria?.prod_tipo_descricao || '',
        quality
      };
      saveDraft(draft);
      if (onSaved) onSaved(draft);
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center p-3 z-50">
      <div className="bg-navy-900 w-full max-w-sm rounded-lg shadow-2xl flex flex-col max-h-[88vh] border border-navy-700">
        <header className="flex items-center justify-between p-3 border-b border-navy-700">
          <h1 className="text-base font-semibold text-yellow-400">Detalhes do Pedido</h1>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-200" aria-label="Fechar">
            <X className="w-4 h-4" />
          </button>
        </header>
        <main className="flex-grow p-4 space-y-4 overflow-y-auto">
          <div>
            <label htmlFor="file-name" className="block text-xs font-medium text-gray-300 mb-1.5">Nome do Arquivo</label>
            <div className="relative">
              <input
                id="file-name"
                type="text"
                className={`w-full h-10 pl-3 pr-8 rounded-md bg-navy-800 text-white placeholder-gray-400 focus:outline-none focus:ring-2 text-sm 
                ${fileName.trim()==='' ? 'focus:ring-yellow-500' : parsed.ok ? 'ring-1 ring-green-500 focus:ring-green-500' : 'ring-1 ring-red-500 focus:ring-red-500'}`}
                placeholder="Cole o nome do arquivo"
                value={fileName}
                onChange={(e) => setFileName(e.target.value)}
                aria-invalid={fileName.trim()!=='' && !parsed.ok}
                aria-describedby="file-name-hint"
              />
              {fileName.trim()!=='' && (
                parsed.ok ? (
                  <CheckCircle2 className="w-4 h-4 text-green-400 absolute right-2 top-1/2 -translate-y-1/2" />
                ) : (
                  <XCircle className="w-4 h-4 text-red-400 absolute right-2 top-1/2 -translate-y-1/2" />
                )
              )}
            </div>
          </div>

          <div className="bg-navy-800/60 border border-navy-700 rounded-md p-3 text-[13px] leading-relaxed text-gray-200">
            <p className="font-semibold mb-1.5">Como o nome do arquivo é interpretado:</p>
            <p className="text-[11px]">Exemplo: <span className="font-mono bg-navy-900 px-1.5 py-0.5 rounded">20251003MARCOSROCHA45265</span></p>
            <ul className="list-disc list-inside mt-1.5 space-y-1 text-[11px]">
              <li><strong>Data:</strong> <span className="font-mono bg-navy-900 px-1.5 py-0.5 rounded">20251003</span> → 03/10/2025</li>
              <li><strong>Cliente:</strong> <span className="font-mono bg-navy-900 px-1.5 py-0.5 rounded">MARCOSROCHA</span> → Marcos Rocha</li>
              <li><strong>Pedido:</strong> <span className="font-mono bg-navy-900 px-1.5 py-0.5 rounded">45265</span> → Nº 45265</li>
            </ul>
            {!parsed.ok && fileName.trim() !== '' && (
              <p id="file-name-hint" className="mt-2 text-[11px] text-red-300">{parsed.error}. Formato esperado: AAAAMMDD[NOME][NNNNN]</p>
            )}
          </div>

          <div>
            <div className="flex items-center justify-between mb-1.5 relative">
              <label htmlFor="service-type" className="block text-xs font-medium text-gray-300">Tipo de Serviço</label>
              <button
                type="button"
                aria-label="Regras de dimensão por produto"
                className="text-gray-400 hover:text-gray-200"
                onMouseEnter={() => setShowRules(true)}
                onMouseLeave={() => setShowRules(false)}
                onFocus={() => setShowRules(true)}
                onBlur={() => setShowRules(false)}
              >
                <Info className="w-3.5 h-3.5" />
              </button>

              {showRules && (
                <div className="absolute right-0 top-5 z-10 w-72 bg-navy-900 border border-navy-700 rounded shadow-lg p-3 text-[11px] text-gray-200 leading-snug">
                  <p className="font-semibold mb-1">{categoriaId ? `Regras (${categorias.find(c => c.id === categoriaId)?.prod_tipo_descricao})` : 'Regras por tipo'}</p>
                  {categoriaId ? (
                    <ul className="list-disc list-inside space-y-0.5">
                      {(CATEGORY_RULES[categoriaId] || []).map((rule) => (
                        <li key={rule}>{rule}</li>
                      ))}
                    </ul>
                  ) : (
                    <ul className="list-disc list-inside space-y-0.5">
                      <li>DTF TEXTIL: 57,5cm x 205cm</li>
                      <li>SUBLIMACAO: 157,0cm x 450cm</li>
                      <li>DTF UV [metro]: 58,5cm x 100cm</li>
                      <li>DTF UV [unidade]: 40,5cm x 28,5cm</li>
                      <li>CAMISETA: sem regras</li>
                    </ul>
                  )}
                </div>
              )}
            </div>
            <select
              id="service-type"
              className={`w-full h-10 px-3 rounded-md bg-navy-800 text-white focus:outline-none focus:ring-2 focus:ring-yellow-500 text-sm ${!categoriaId && 'text-gray-400'}`}
              value={categoriaId || ''}
              onChange={(e) => setCategoriaId(e.target.value ? Number(e.target.value) : null)}
              disabled={carregandoCategorias}
            >
              <option value="" disabled hidden>
                {carregandoCategorias ? 'Carregando...' : 'Selecione…'}
              </option>
              {categorias.map((cat) => (
                <option key={cat.id} value={cat.id}>{cat.prod_tipo_descricao}</option>
              ))}
            </select>
          </div>

          <div>
            <label htmlFor="quality" className="block text-xs font-medium text-gray-300 mb-1.5">Qualidade</label>
            <select
              id="quality"
              className="w-full h-10 px-3 rounded-md bg-navy-800 text-white focus:outline-none focus:ring-2 focus:ring-yellow-500 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              value={quality}
              onChange={(e) => setQuality(e.target.value)}
              disabled={!categoriaId}
            >
              {!categoriaId && <option value="">Selecione o tipo de serviço primeiro</option>}
              {categoriaId && qualities.map((q) => (
                <option key={q} value={q}>{q}</option>
              ))}
            </select>
          </div>
        </main>
        <footer className="p-3 bg-navy-900 border-t border-navy-700 flex items-center justify-end gap-2">
          <button
            className="px-4 py-2 text-xs font-medium text-gray-300 rounded-md hover:bg-navy-800"
            onClick={onClose}
          >
            Cancelar
          </button>
          <button
            className={`h-10 px-5 rounded-md font-semibold text-sm ${isValid ? 'bg-yellow-400 text-navy-900 hover:bg-yellow-300' : 'bg-yellow-400/40 text-navy-900/60 cursor-not-allowed'}`}
            onClick={handleSave}
            disabled={!isValid || saving}
          >
            {saving ? 'Salvando…' : 'Salvar'}
          </button>
        </footer>
      </div>
    </div>
  );
}


